// TODO: use react-query and create the fetches in an api folder
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuthContext } from '../../hooks/useAuthContext'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const useStaffInvitations = () => {
	const [urlParams, setUrlParams] = useSearchParams()
	const queryClient = useQueryClient()
	const { user, dispatch } = useAuthContext()
	const [invitation, setInvitation] = useState<any>()
	const [show, setShow] = useState(false)
	const [index, setIndex] = useState<any>()

	const close = () => {
		setShow(false)

		// NOTE: allows user to accept multiple invitations
		if (invitations.length > index + 1) {
			setTimeout(() => {
				setIndex(index + 1)
				setInvitation(invitations[index + 1])
				setShow(true)
			}, 500) // wait 500ms to open next modal for a smooth transition
		}
	}

	const { isLoading: isLoadingInvitation, isError: isErrorInvitation, error: errorInvitation, data: invitations } = useQuery({
		queryKey: ['staffInvitations'],
		queryFn: async () => {
			const response = await fetch(`/api/health/api/teams/staff/invitations`, {
				headers: {
					Authorization: `Bearer ${user.token}`
				}
			})

			const json = await response.json()

			if (!response.ok) throw new Error(json.error)

			// get teamToken search
			const token = urlParams.get('teamToken')

			// if token exists, get team through fetch function
			let tokenJson: any
			if (token) {
				const tokenResp = await fetch(`/api/health/api/teams/token/${token}`, {
					headers: {
						Authorization: `Bearer ${user.token}`
					}
				})

				tokenJson = await tokenResp.json()

				if (!tokenResp.ok) return toast.error("Couldn't load your token", { description: tokenJson.error })
			}

			// a manager added them to a team
			if (json?.length > 0) {
				// if they also have a direct link invitation added to the end of the invitations
				queryClient.setQueryData(['staffInvitations'], () => tokenJson ? [...json, tokenJson] : json)
				setShow(true)
				setIndex(0)
				setInvitation(json[0])
			}

			// no invitations but a direct link
			if ((!json || json.length === 0) && tokenJson) {
				queryClient.setQueryData(['staffInvitations'], () => [tokenJson])
				setShow(true)
				setIndex(0)
				setInvitation(tokenJson)
			}

			return json ?? null
		}
	})

	const { isPending: isLoadingAccepting, mutate: accept } = useMutation({
		mutationFn: async () => {
			const response = await fetch(`/api/health/api/teams/staff/join`, {
				method: 'PATCH',
				body: JSON.stringify({
					teamId: invitation._id,
					token: urlParams.get('teamToken')
				}),
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${user.token}`
				}
			})

			const json = await response.json()

			if (!response.ok) throw new Error(json.error)

			return json ?? null
		},
		onSuccess: (json: any) => {
			const updatedUser = {
				...user,
				team: json.team
			}
			localStorage.setItem('user', JSON.stringify(updatedUser))
			dispatch({
				type: 'LOGIN',
				payload: updatedUser
			})

			urlParams.delete('teamToken')
			setUrlParams(urlParams)

			// NOTE: everything read for the previous team is fetched again for the new one
			queryClient.invalidateQueries()

			toast.success(`You joined ${invitation.name}`)

			close()
		},
		onError: (error) => {
			toast.error("Couldn't accept invitation", { description: error.message })
		}
	})

	const { isPending: isLoadingDecline, mutate: decline } = useMutation({
		mutationFn: async () => {
			const response = await fetch(`/api/health/api/teams/staff/decline`, {
				method: 'PATCH',
				body: JSON.stringify({
					teamId: invitation._id
				}),
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${user.token}`
				}
			})

			const json = await response.json()

			if (!response.ok) throw new Error(json.error)

			return json ?? null
		},
		onSuccess: () => {
			// NOTE: remove token search
			urlParams.delete('teamToken')
			setUrlParams(urlParams)

			close()
		},
		onError: (error) => {
			toast.error("Couldn't decline invitation", { description: error.message })
		}
	})

	return {
		invitation,
		isLoadingInvitation,
		errorInvitation,
		isErrorInvitation,
		show,
		isLoadingAccepting,
		isLoadingDecline,
		close,
		accept,
		decline
	}
}

export default useStaffInvitations
